import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPut, apiPost } from '../lib/api.js';
import TopBar from '../components/TopBar.jsx';
import { Shuffle, Users, RotateCw, Save, Activity } from 'lucide-react';
import toast from 'react-hot-toast';

const STRATEGIES = [
  { value: 'round_robin', label: 'Round robin', desc: 'Even distribution across all available agents' },
  { value: 'shark_tank', label: 'Shark tank', desc: 'Race — first agent to claim wins' },
  { value: 'weighted', label: 'Weighted', desc: 'Higher-priority agents get more leads' },
  { value: 'state_match', label: 'State match', desc: 'Lead state must match agent licensed states' },
];

export default function LeadDistribution() {
  const qc = useQueryClient();
  const { data: cfgData } = useQuery({ queryKey: ['ld-config'], queryFn: () => apiGet('/lead-distribution/config') });
  const { data: statsData } = useQuery({ queryKey: ['ld-stats'], queryFn: () => apiGet('/lead-distribution/stats'), refetchInterval: 30_000 });

  const [c, setC] = useState({ enabled: false, strategy: 'round_robin', inactivityTimeoutMinutes: 60, maxLeadsPerAgent: 0 });
  useEffect(() => { if (cfgData?.config) setC({
    enabled: !!cfgData.config.enabled,
    strategy: cfgData.config.strategy || 'round_robin',
    inactivityTimeoutMinutes: cfgData.config.inactivityTimeoutMinutes ?? 60,
    maxLeadsPerAgent: cfgData.config.maxLeadsPerAgent ?? 0,
  }); }, [cfgData]);

  async function save() { try { await apiPut('/lead-distribution/config', c); toast.success('Saved'); qc.invalidateQueries({ queryKey: ['ld-config'] }); } catch (e) { toast.error(e.message); } }
  async function cycleNow() { try { const r = await apiPost('/lead-distribution/cycle-now', {}); toast.success(`Cycled ${r.cycled || 0} leads`); qc.invalidateQueries({ queryKey: ['ld-stats'] }); } catch (e) { toast.error(e.message); } }

  return (
    <div className="space-y-4">
      <TopBar title="Lead Distribution" subtitle="Round-robin · Cycle · Per-agent caps" />
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="panel p-4 space-y-4">
          <div className="flex items-center gap-2 text-sm text-white/70"><Shuffle className="w-4 h-4" /> Distribution config</div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={c.enabled} onChange={(e) => setC({ ...c, enabled: e.target.checked })} />
            <span>Enable auto-distribution</span>
          </label>
          <div>
            <label className="block text-xs text-white/60 mb-1">Strategy</label>
            <div className="space-y-2">
              {STRATEGIES.map((s) => (
                <label key={s.value} className={`flex gap-3 p-3 rounded-md border cursor-pointer ${c.strategy === s.value ? 'border-civix-purple/50 bg-civix-purple/10' : 'border-white/5 hover:border-white/15'}`}>
                  <input type="radio" name="strategy" checked={c.strategy === s.value} onChange={() => setC({ ...c, strategy: s.value })} className="mt-1" />
                  <div><div className="text-sm">{s.label}</div><div className="text-xs text-white/50">{s.desc}</div></div>
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-white/60 mb-1">Inactivity timeout (min)</label>
              <input type="number" min="5" value={c.inactivityTimeoutMinutes} onChange={(e) => setC({ ...c, inactivityTimeoutMinutes: Number(e.target.value) })} className="input w-full" />
              <div className="text-xs text-white/40 mt-1">Unworked lead returns to pool after this many minutes</div>
            </div>
            <div>
              <label className="block text-xs text-white/60 mb-1">Max leads per agent</label>
              <input type="number" min="0" value={c.maxLeadsPerAgent} onChange={(e) => setC({ ...c, maxLeadsPerAgent: Number(e.target.value) })} className="input w-full" />
              <div className="text-xs text-white/40 mt-1">0 = unlimited</div>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={save} className="btn-primary inline-flex items-center gap-2"><Save className="w-4 h-4" /> Save config</button>
            <button onClick={cycleNow} className="btn inline-flex items-center gap-2"><RotateCw className="w-4 h-4" /> Run cycle now</button>
          </div>
        </div>

        <div className="panel p-0 overflow-hidden">
          <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2 text-sm text-white/70"><Users className="w-4 h-4" /> Agent pool</div>
          <table className="w-full text-sm">
            <thead className="text-left text-white/50 text-xs">
              <tr><th className="px-4 py-2">Agent</th><th>Role</th><th>Available</th><th className="text-right pr-4">Leads</th></tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {(statsData?.agents || []).map((a) => (
                <tr key={a.agentId}>
                  <td className="px-4 py-2"><div>{a.name}</div><div className="text-xs text-white/50">{a.email}</div></td>
                  <td className="text-white/70">{a.role}</td>
                  <td>{a.available ? <span className="text-emerald-400">●</span> : <span className="text-white/30">○</span>}</td>
                  <td className="text-right pr-4 font-mono">{a.leadCount}</td>
                </tr>
              ))}
              {(statsData?.agents || []).length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-white/50 text-center">No active agents</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
